import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CustomerAppUser,
  CustomerAppUserService,
} from '@api/services/customer-app-user.service';
import { IndexResponse } from '@api/models/response.model';
import { CardComponent } from '@ui/card/card.component';
import { InputComponent } from '@ui/input/input.component';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { ModalService } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';
import { CustomerAppUsersModalComponent } from './customer-app-users-modal/customer-app-users-modal.component';

@Component({
  selector: 'app-customer-app-users',
  standalone: true,
  imports: [CardComponent, TableComponent, InputComponent, FormsModule],
  template: `
    <div class="flex flex-col gap-4">
      <ui-card>
        <div>
          <h1 class="text-lg text-slate-900">App-gebruikers</h1>
          <p class="text-sm text-slate-500 mt-1">
            Gebruikers van de klanten-app (AFAS-contactpersonen). Accounts worden automatisch
            gesynchroniseerd. Zoek op naam, e-mail of AFAS-id.
          </p>
        </div>
        <div class="mt-4 max-w-md">
          <ui-input
            label="Zoeken"
            placeholder="Naam, e-mail, AFAS-id..."
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchChange()"
          ></ui-input>
        </div>
      </ui-card>

      <ui-table
        [columns]="columns"
        [data]="data()"
        [loading]="loading()"
        (onPageChange)="onPageChange($event)"
        [showPagination]="true"
        [meta]="meta()"
      ></ui-table>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerAppUsersComponent implements OnInit {
  private customerAppUserService = inject(CustomerAppUserService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);

  searchQuery = '';
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  columns: TableColumn[] = [
    { key: 'name', label: 'Naam' },
    { key: 'email', label: 'E-mail' },
    {
      key: 'profile.afas_id',
      label: 'AFAS-id',
      format: (_value: unknown, row: CustomerAppUser) =>
        row.profile?.afas_id ?? '—',
    },
    {
      key: 'blocked',
      label: 'Status',
      format: (value: boolean) => (value ? 'Geblokkeerd' : 'Actief'),
    },
    {
      key: 'has_logged_in',
      label: 'Ingelogd',
      format: (value: boolean) => (value ? 'Ja' : 'Nee'),
    },
    {
      key: 'last_login_at',
      label: 'Laatste login',
      format: (value: string | null) =>
        value ? new Date(value).toLocaleString('nl-NL') : '—',
    },
    {
      key: 'actions',
      label: '',
      width: '140px',
      actions: [
        {
          label: 'Bewerken',
          icon: 'edit',
          variant: 'primary',
          onClick: (row) => this.editUser(row as CustomerAppUser),
        },
      ],
    },
  ];

  data = signal<CustomerAppUser[]>([]);
  loading = signal(false);
  meta = signal<IndexResponse<CustomerAppUser>['meta'] | null>(null);

  ngOnInit() {
    this.getUsers(1);
  }

  onSearchChange() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => this.getUsers(1), 300);
  }

  getUsers(page: number = 1) {
    this.loading.set(true);
    this.customerAppUserService.getUsers(page, this.searchQuery).subscribe({
      next: (response) => {
        this.data.set(response.data);
        this.meta.set(response.meta);
        this.loading.set(false);
      },
      error: (error) => {
        const message =
          error?.error?.message ||
          'Er is iets fout gegaan bij het ophalen van app-gebruikers';
        this.notificationService.error(message);
        this.loading.set(false);
      },
    });
  }

  onPageChange(page: number) {
    this.getUsers(page);
  }

  editUser(user: CustomerAppUser) {
    const modalRef = this.modalService.open(CustomerAppUsersModalComponent, {
      data: { user },
    });

    modalRef.onClose.subscribe(() => {
      this.getUsers(this.meta()?.current_page ?? 1);
    });
  }
}
